import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  Collapse,
  Alert,
  Snackbar,
} from "@mui/material";
import { Visibility, VisibilityOff, Email, Lock } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiEndpoints from "../../../apiconfig";

const authImage =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80";

const Login = ({ setIsAuthenticated = () => { }, showSnackbar = () => { } }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600px - 900px
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    identifier: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // OTP Verification State
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [token, setToken] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "visible";
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.identifier) {
      newErrors.identifier = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.identifier)) {
      newErrors.identifier = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await fetch(apiEndpoints.login, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          identifier: formData.identifier,
          password: formData.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Login failed");
      }

      if (data.success) {
        setToken(data.token);
        if (data.isverified === 1) {
          sessionStorage.setItem("token", data.token);
          setIsAuthenticated(true);
          showSnackbar("Login successful", "success");
          navigate("/dashboard");
        } else {
          try {
            const response = await fetch(apiEndpoints.sendotp, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: formData.identifier,
                purpose: "login",
              }),
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.error || "Failed to resend OTP");
            }

            // Reset countdown
            setOtpCountdown(60);
            const countdownInterval = setInterval(() => {
              setOtpCountdown((prev) => {
                if (prev <= 1) clearInterval(countdownInterval);
                return prev - 1;
              });
            }, 1000);

            setSnackbar({
              open: true,
              message: "OTP resent successfully",
              severity: "success",
            });
          } catch (error) {
            setSnackbar({
              open: true,
              message: error.message || "Failed to resend OTP",
              severity: "error",
            });
          }

          // User needs verification
          setRegisteredEmail(formData.identifier);
          setShowOtpSection(true);

          // Start OTP countdown timer
          setOtpCountdown(60);
          const countdownInterval = setInterval(() => {
            setOtpCountdown((prev) => {
              if (prev <= 1) clearInterval(countdownInterval);
              return prev - 1;
            });
          }, 1000);

          setSnackbar({
            open: true,
            message:
              "Please verify your account with the OTP sent to your email",
            severity: "info",
          });
        }
      } else {
        showSnackbar(data.error || "Login failed", "error");
      }
    } catch (error) {
      console.error("Login error:", error);
      showSnackbar(error.message || "An error occurred during login", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await fetch(apiEndpoints.sendotp, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.identifier,
          purpose: "login",
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to resend OTP");
      }

      // Reset countdown
      setOtpCountdown(60);
      const countdownInterval = setInterval(() => {
        setOtpCountdown((prev) => {
          if (prev <= 1) clearInterval(countdownInterval);
          return prev - 1;
        });
      }, 1000);

      setSnackbar({
        open: true,
        message: "OTP resent successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to resend OTP",
        severity: "error",
      });
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setSnackbar({
        open: true,
        message: "Please enter a valid 6-digit OTP",
        severity: "error",
      });
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(apiEndpoints.verifyotp, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: registeredEmail,
          otp: otp,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "OTP verification failed");
      }

      // If verification successful, proceed with login
      sessionStorage.setItem("token", token);
      setIsAuthenticated(true);
      showSnackbar("Account verified successfully!", "success");
      navigate("/dashboard");
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "OTP verification failed",
        severity: "error",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  // Styles - Freelancer.in Theme
  const containerStyle = {
    padding: "0px !important",
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F5F7FA", // Freelancer Light Gray
    py: isMobile ? 2 : 4,
    px: isMobile ? 1.5 : 4,
  };

  const paperStyle = {
    display: "flex",
    borderRadius: "16px", // Sharper but rounded
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)", // Softer, deeper shadow
    width: "100%",
    maxWidth: isTablet ? 700 : 1000,
    backgroundColor: "#ffffff",
  };

  const formContainerStyle = {
    p: isMobile ? 3 : isTablet ? 4 : 6,
    width: "100%",
    maxWidth: isTablet ? "100%" : 500,
    backgroundColor: "#ffffff",
  };

  const imageContainerStyle = {
    display: isMobile ? "none" : "block",
    width: isTablet ? "40%" : "50%",
    minWidth: isTablet ? 300 : 500,
    backgroundImage: `url(${authImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background:
        "rgba(15, 23, 42, 0.9)", // Modern flat dark overlay
      opacity: 0.9,
    },
  };

  const imageContentStyle = {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    // p: isTablet ? 3 : 4,
    px: isTablet ? 3 : 4,
    color: "white",
    textAlign: "center",
  };

  const titleStyle = {
    textAlign: "center",
    mb: 1,
    fontWeight: 700,
    fontSize: isMobile ? "1.75rem" : "2rem",
    color: "#111827", // Dark Gray
    letterSpacing: "-0.01em",
  };

  const subtitleStyle = {
    textAlign: "center",
    mb: isMobile ? 3 : 4,
    color: "#4B5563", // Medium Gray
    fontSize: isMobile ? "0.9rem" : "1rem",
    fontWeight: 500,
    lineHeight: 1.6,
  };

  // const textFieldStyle = {
  //   width: "100%",
  //   "& .MuiOutlinedInput-root": {
  //     borderRadius: 2,
  //     backgroundColor: "#ffffff",
  //     border: "1px solid #e2e8f0",
  //     transition: "all 0.2s ease",
  //     "&:hover": {
  //       borderColor: "#000000ff",
  //     },
  //     // "&.Mui-focused": {
  //     //   borderColor: "#000000ff",
  //     //   boxShadow: "0 0 0 2px rgba(99, 102, 241, 0.2)",
  //     // },
  //   },
  //   "& .MuiInputLabel-root": {
  //     color: "#64748b",
  //     fontSize: isMobile ? "0.9rem" : "1rem",
  //     "&.Mui-focused": {
  //       color: "#000000ff",
  //     },
  //   },
  //   "& .MuiOutlinedInput-notchedOutline": {
  //     border: "none",
  //   },
  //   "& .MuiInputBase-input": {
  //     fontSize: isMobile ? "0.9rem" : "1rem",
  //     padding: isMobile ? "12px 14px" : "14px 16px",
  //   },
  // };

  const textFieldStyle = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#ffffff",
      transition: "all 0.2s ease-in-out",
      "& fieldset": {
        borderColor: "#E5E7EB", // Light gray border
      },
      "&:hover fieldset": {
        borderColor: "#9CA3AF",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#1967D2", // Freelancer Blue
        borderWidth: "1px",
        boxShadow: "0 0 0 3px rgba(25, 103, 210, 0.1)", // Subtle Glow
      },
      // Add alignment for input content
      alignItems: "center",
    },
    "& .MuiInputLabel-root": {
      color: "#6B7280",
      fontSize: isMobile ? "0.9rem" : "1rem",
      "&.Mui-focused": {
        color: "#1967D2",
      },
    },
    "& .MuiInputBase-input": {
      fontSize: isMobile ? "0.9rem" : "1rem",
      padding: isMobile ? "12px 14px" : "14px 16px",
      // Ensure proper vertical alignment
      paddingLeft: "8px",
      color: "#1F2937",
    },
    // Style for the input adornments
    "& .MuiInputAdornment-root": {
      marginRight: "8px",
      "& .MuiSvgIcon-root": {
        color: "#9CA3AF",
        fontSize: isMobile ? "20px" : "22px",
      },
    },
  };

  const buttonStyle = {
    py: isMobile ? 0.8 : 1,
    px: isMobile ? 8 : 15,
    fontWeight: 600,
    fontSize: isMobile ? "1rem" : "1.05rem",
    borderRadius: "8px",
    textTransform: "none",
    backgroundColor: "#1967D2", // Freelancer Blue
    color: "white",
    transition: "all 0.2s ease",
    width: "100%",
    boxShadow: "0 4px 6px -1px rgba(25, 103, 210, 0.2)",
    "&:hover": {
      backgroundColor: "#1557B0",
      boxShadow: "0 10px 15px -3px rgba(25, 103, 210, 0.3)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      backgroundColor: "#F3F4F6",
      color: "#9CA3AF",
      boxShadow: "none",
    },
  };

  const linkStyle = {
    textDecoration: "none",
    fontWeight: 600,
    color: "#1967D2", // Freelancer Blue
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#1557B0",
      textDecoration: "underline",
    },
  };

  const dividerStyle = {
    width: "100%",
    color: "#e2e8f0",
    my: 2,
    "&::before, &::after": {
      borderColor: "#e2e8f0",
    },
  };

  const loginFooterTextStyle = {
    textAlign: "center",
    color: "#64748b",
    fontSize: isMobile ? "0.8rem" : "0.9rem",
    mt: 3,
  };

  const otpSectionStyle = {
    mt: 2,
    p: 2,
    border: "1px solid #e2e8f0",
    borderRadius: 2,
    backgroundColor: "#f8fafc",
  };

  return (
    <Box sx={containerStyle}>
      <Paper elevation={0} sx={paperStyle}>
        {/* Left side - Form content */}
        <Box sx={formContainerStyle}>
          <Typography variant="h3" sx={titleStyle}>
            Welcome Back
          </Typography>

          <Typography variant="body1" sx={subtitleStyle}>
            Sign in to your account to continue
          </Typography>

          <Collapse in={!showOtpSection}>
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                label="Email Address"
                name="identifier"
                type="email"
                value={formData.identifier}
                onChange={handleChange}
                error={!!errors.identifier}
                helperText={errors.identifier}
                // margin="normal"
                sx={{ ...textFieldStyle, mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email
                        sx={{ color: "#94a3b8", fontSize: isMobile ? 20 : 22 }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                // margin="normal"
                sx={textFieldStyle}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock
                        sx={{ color: "#94a3b8", fontSize: isMobile ? 20 : 22 }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{
                          color: "#94a3b8",
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      >
                        {showPassword ? (
                          <VisibilityOff
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        ) : (
                          <Visibility
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: "right", mb: 1 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() => navigate("/forgot-password")}
                  sx={linkStyle}
                >
                  Forgot Password?
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                sx={buttonStyle}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>
          </Collapse>

          {/* OTP Verification Section */}
          <Collapse in={showOtpSection}>
            <Box sx={otpSectionStyle}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Verify Your Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We've sent a 6-digit verification code to {registeredEmail}.
                Please enter it below:
              </Typography>

              <TextField
                fullWidth
                label="OTP Code"
                variant="outlined"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />

              <Button
                onClick={handleResendOtp}
                disabled={otpCountdown > 0}
                sx={{
                  textTransform: "none",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: otpCountdown > 0 ? "#9CA3AF" : "#1967D2",
                  mb: 2,
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                {otpCountdown > 0 ? `Resend in ${otpCountdown}s` : "Resend OTP"}
              </Button>

              <Button
                onClick={handleVerifyOtp}
                disabled={!otp || otp.length !== 6 || isVerifying}
                variant="contained"
                fullWidth
                sx={buttonStyle}
              >
                {isVerifying ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Verify OTP"
                )}
              </Button>
            </Box>
          </Collapse>

          <Typography
            variant="body2"
            sx={{
              textAlign: "center",
              color: "#64748b",
              mt: 2,
              fontSize: isMobile ? "0.9rem" : "1rem",
            }}
          >
            Don't have an account?{" "}
            <Link
              component="button"
              type="button"
              onClick={() => navigate("/register")}
              sx={linkStyle}
            >
              Sign Up
            </Link>
          </Typography>

          <Typography variant="body2" sx={loginFooterTextStyle}>
            By continuing, you agree to our Terms of Service and Privacy Policy
          </Typography>
        </Box>

        {/* Right side - Image */}
        <Box sx={imageContainerStyle}>
          <Box sx={imageContentStyle}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Post Jobs with Confidence
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Find the right freelancer for your project in minutes. Post your job,
              set your requirements, and connect with top talent ready to deliver
              quality results—on time and within budget.
            </Typography>
          </Box>
        </Box>

      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Login;
